import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useState, forwardRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { CloudArrowUpIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import Picker from "react-datepicker";

import Title from "../LoanTermDeviation/Title";
import Button from "../../components/atomic/Button";
import TextArea from "../../components/atomic/TextArea";
import DropdownWithSearch from "../../components/atomic/DropdownWithSearch";
import ApplicantSelectionDropdown from "../../components/composed/ApplicantSelectionDropdown";
import FileUploadInput from "../../components/composed/FormElements/FileUploadInput";
import UploadOptionsModal from "../../components/composed/UploadOptionsModal";
import CameraCapture from "../../components/composed/CameraCapture";
import Input from "../../components/composed/FormElements/Input";

import { LoanApplicationService } from "../../services/LoanApplication";
import { CommonService } from "../../services/Common";
import UploadedDocumentView from "../../components/composed/UploadedDocumentView";
import { UiConfigService } from "../../services/UiConfig";
import { getUserRbacGroup } from "../../shared/utils/Global";

import { ORIGINAL_XEROX_OPTIONS } from "./AllDocuments.constant";
import { FileUploadRejectionReason } from "../../components/composed/FormElements/constants";

interface IDocumentSlider {
  pageName: string;
  token: string;
  isLenderDashboard: boolean;
  useParams: any;
  userID?: any;
  reloadParent?: any;
  isOpen?: any;
  close: () => void;
  setCategory?: any;
  forCAMReportOnly?: boolean;
  styles?: {
    container?: string;
  };
  location: any;
  isMFL_LAP?: boolean;
  studioExperience: boolean;
  notify: any;
  sourceEntityID?: string;
  loanDetails?: any;
  editMode?: boolean;
  editModeMetadata?: {
    date?: string;
    details?: string;
    originalXerox?: string;
    [key: string]: any;
  };
  editModeMediaID?: string;
}

const CAM_REPORT_MEDIA_ID = "36da56b3-df3e-4dee-8e93-c58f19b8a694";

interface FormValues {
  applicant: string | { name: string; value: string };
  category: string | { name: string; value: string };
  documentName: string | { name: string; value: string };
  file: File | null;
  comments: string;
  scheduleDate: Date | null;
  scheduleDetails: string;
  scheduleOriginalXerox: string | { name: string; value: string };
}

const CustomDateInput = forwardRef<
  HTMLButtonElement,
  {
    value: string;
    onClick: () => void;
    disabled: boolean;
    requiredIcon?: boolean;
  }
>(({ value, onClick, disabled, requiredIcon }, ref) => (
  <Button
    type="button"
    onClick={onClick}
    ref={ref}
    style={{ all: "unset", display: "block", width: "100%", cursor: "pointer" }}
  >
    <Input
      placeholder="Enter Date"
      label="Date"
      value={value}
      readOnly
      disabled={disabled}
      requiredIcon={requiredIcon}
    />
  </Button>
));

CustomDateInput.displayName = "CustomDateInput";

const AddNewDocumentSlider = (documentSliderProps: IDocumentSlider) => {
  const {
    forCAMReportOnly,
    useParams,
    isMFL_LAP,
    isLenderDashboard,
    notify,
    userID,
    studioExperience,
    sourceEntityID,
    loanDetails,
    editMode = false,
    editModeMetadata,
    editModeMediaID
  } = documentSliderProps;

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {
      applicant: "",
      category: "",
      documentName: "",
      file: null,
      comments: "",
      scheduleDate: null,
      scheduleDetails: "",
      scheduleOriginalXerox: ""
    }
  });

  const file = watch("file");
  const uploadedFile = watch("file");
  const selectedCategory = watch("category");
  const documentName = watch("documentName");

  const resolvedDocID = forCAMReportOnly
    ? CAM_REPORT_MEDIA_ID
    : (documentName as any)?.value || documentName;

  const { loanID } = useParams();

  const [details, setDetails] = useState<string>("");
  const [fileView, setFileView] = useState<boolean>(false);
  const [object, setObject] = useState<File | null>(null);
  const [objectPath, setObjectPath] = useState<string | null>(null);
  const [selectedApplicant, setSelectedApplicant] = useState<any>(null);
  const [showUploadOptions, setShowUploadOptions] = useState<boolean>(false);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [fileInputOpen, setFileInputOpen] = useState<(() => void) | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showOverLayAddress, setShowOverLayAddress] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [scheduleDCategorySelected, setScheduleDCategorySelected] =
    useState<boolean>(false);
  const [originalXerox, setOriginalXerox] = useState<
    string | { name: string; value: string }
  >("");
  const [categories, setCategories] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [documentNames, setDocumentNames] = useState<
    {
      label: string;
      value: string;
      duplicateEntryAllowed: boolean;
      restrictUpload: boolean;
    }[]
  >([]);
  const [userUploadsRemaining, setUserUploadsRemaining] = useState<
    Record<string, number> | undefined
  >();

  const rbacGroupName = getUserRbacGroup();

  const isBCMUser = rbacGroupName === "BCM Group";
  const isCOUser = rbacGroupName === "CO Group";
  const isLoanSanctioned =
    loanDetails?.taskStatus?.toLowerCase() === "loan_sanction";

  const showMetadataFields = isBCMUser || (isCOUser && isLoanSanctioned);

  const activeUserID =
    isMFL_LAP && selectedApplicant?.value ? selectedApplicant.value : userID;

  const maxFilesAllowed =
    userUploadsRemaining &&
    resolvedDocID &&
    resolvedDocID in userUploadsRemaining
      ? Math.min(userUploadsRemaining[resolvedDocID], 5)
      : 1;

  const isUploadRestricted = maxFilesAllowed <= 0;

  async function fetchUploadLimits(id: string) {
    const payload = {
      userID: id,
      loanApplicationID: loanID,
      CustomCategories: [],
      filteredCategories: [],
      ReturnAllLeafNodes: true
    };

    const response: any = await LoanApplicationService.getDocuments({
      token: documentSliderProps.token,
      pageName: documentSliderProps.pageName,
      payload,
      isLenderDashboard: isLenderDashboard || false,
      sourceEntityID
    });

    if (response?.status) {
      setUserUploadsRemaining(response?.data?.allowedUploadsRemaining);
    }
  }

  useEffect(() => {
    if (documentSliderProps.isOpen && !forCAMReportOnly) getCategory();

    return () => {
      if (!forCAMReportOnly && !editMode) {
        resetValues();
        reset();
      }
    };
  }, [documentSliderProps.isOpen, forCAMReportOnly]);

  // Populate form with existing metadata values when in edit mode
  useEffect(() => {
    if (editMode && editModeMetadata && documentSliderProps.isOpen) {
      setScheduleDCategorySelected(true);

      if (editModeMetadata.date) {
        const dateObj = new Date(editModeMetadata.date);
        dateObj.setHours(12, 0, 0, 0);
        setSelectedDate(dateObj);
        setValue("scheduleDate", dateObj);
      }

      if (editModeMetadata.details) {
        setDetails(editModeMetadata.details);
        setValue("scheduleDetails", editModeMetadata.details);
      }

      if (editModeMetadata.originalXerox) {
        const originalXeroxValue =
          typeof editModeMetadata.originalXerox === "string"
            ? {
                name: editModeMetadata.originalXerox,
                value: editModeMetadata.originalXerox
              }
            : editModeMetadata.originalXerox;
        setOriginalXerox(originalXeroxValue);
        setValue("scheduleOriginalXerox", originalXeroxValue);
      }
    }
  }, [
    editMode,
    editModeMetadata,
    documentSliderProps.isOpen,
    selectedCategory,
    setValue
  ]);

  // Show the three metadata fields when the category is Schedule D.
  useEffect(() => {
    if (selectedCategory) {
      const docNameValue =
        typeof selectedCategory === "string"
          ? selectedCategory
          : selectedCategory?.name;
      const isScheduleD = docNameValue === "Schedule D";
      setScheduleDCategorySelected(isScheduleD);

      // Clear the three metadata fields when switching away from Schedule D
      if (!isScheduleD) {
        setSelectedDate(null);
        setDetails("");
        setOriginalXerox("");
        setValue("scheduleDate", null);
        setValue("scheduleDetails", "");
        setValue("scheduleOriginalXerox", "");
      }
    }
  }, [selectedCategory]);

  async function getCategory() {
    setShowOverLayAddress(false);
    const payload = {
      filter: [],
      resourceName: "document_dropdown",
      dropdownName: "default",
      loanApplicationID: loanID
    };

    const response = (await LoanApplicationService.fetchCategoryList({
      pageName: documentSliderProps.pageName,
      token: documentSliderProps.token,
      payload: payload,
      isPlatformDashboard: !isLenderDashboard,
      sourceEntityID
    })) as any;

    if (response?.status) {
      documentSliderProps.setCategory(response?.data);
      setCategories(response?.data);
    }
  }

  async function getDocumentsByCategory(document: any) {
    setShowOverLayAddress(false);
    const payload = {
      filter: [document.value],
      resourceName: "document_dropdown",
      dropdownName: "default",
      loanApplicationID: loanID
    };

    const response = (await LoanApplicationService.fetchDocumentListByCategory({
      pageName: documentSliderProps.pageName,
      token: documentSliderProps.token,
      payload: payload,
      isPlatformDashboard: !isLenderDashboard,
      sourceEntityID
    })) as any;

    if (response?.status) setDocumentNames(response?.data);
    else {
      notify({
        title: "Error",
        type: "error",
        text: response.error
      });
    }
  }

  async function uploadLenderMedia(fileToUpload: File) {
    const formData = new FormData();
    let location = null;

    // Try to get location but don't block API call if it fails
    if (showOverLayAddress) {
      try {
        location = await populateLocation();
      } catch (error) {
        console.log("Location not available:", error);
      }
    }

    if (file) formData.append("file", fileToUpload);
    formData.append(
      "mediaType",
      "Additional_Docs"
      // forCAMReportOnly ? "CAM Report" : "Additional_Docs"
    );

    formData.append(
      "userID",
      (isMFL_LAP && studioExperience) || userID?.length > 0
        ? userID
        : selectedApplicant?.value //!isMFL_LAP ? documentSliderProps.userID : selectedApplicant?.value,
    );

    formData.append(
      "documentID",
      forCAMReportOnly
        ? CAM_REPORT_MEDIA_ID
        : (documentName as any)?.value || documentName
    );

    const metaData: Record<string, any> = {
      ...(selectedDate
        ? { date: selectedDate.toISOString().split("T")[0] }
        : {}),
      ...(details ? { details: details } : {}),
      ...(originalXerox
        ? {
            originalXerox:
              typeof originalXerox === "object"
                ? originalXerox.value
                : originalXerox
          }
        : {})
    };

    if (showOverLayAddress) {
      metaData["latitude"] = String(location?.latitude);
      metaData["longitude"] = String(location?.longitude);
      metaData["isOverlay"] = showOverLayAddress ? "true" : "false";
    }

    formData.append("metadata", JSON.stringify(metaData));

    const response = await CommonService.uploadDashboardMedia({
      token: documentSliderProps.token,
      formData: formData as any,
      pageName: documentSliderProps.pageName,
      isLenderPlatform: documentSliderProps.isLenderDashboard
    });

    if (response.status) return (response.data as any).mediaID;
    else {
      notify({
        title: "Error",
        type: "error",
        text: response.error
      });
    }
  }

  async function populateLocation() {
    const promise = new Promise<GeolocationCoordinates>((resolve, reject) => {
      const showPosition: PositionCallback = (position) => {
        resolve(position.coords);
      };

      const showError: PositionErrorCallback = (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject("User denied the request for Geolocation.");
            break;
          case error.POSITION_UNAVAILABLE:
            reject("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            reject("The request to get user location timed out.");
            break;
          // case error.UNKNOWN_ERROR:
          //   reject("An unknown error occurred.");
          //   break;
          default:
            reject("An unknown error occurred.");
            break;
        }
      };

      if (navigator.geolocation)
        navigator.geolocation.getCurrentPosition(showPosition, showError);
      else reject("Geolocation is not supported by this browser.");
    });

    const geoData = await promise;

    return geoData;
  }

  const getNotificationText = (uploadedMediaIDs: string[]) =>
    forCAMReportOnly
      ? "CAM Report uploaded successfully and will be available under all documents"
      : `${uploadedMediaIDs.length} document${uploadedMediaIDs.length > 1 ? "s" : ""} uploaded successfully`;

  const onSubmit = async (data: any) => {
    // Handle edit mode - update metadata only
    if (editMode && editModeMediaID) {
      const metaData: Record<string, any> = {
        ...(selectedDate
          ? { date: selectedDate.toISOString().split("T")[0] }
          : {}),
        ...(details ? { details: details } : {}),
        ...(originalXerox
          ? {
              originalXerox:
                typeof originalXerox === "object"
                  ? originalXerox.value
                  : originalXerox
            }
          : {})
      };

      const uiConfigService = new UiConfigService();
      const response = await uiConfigService.executeUIConfig(
        documentSliderProps.token,
        {
          loanApplicationID: loanID,
          resourceName: "upsert_media_metadata",
          userID: userID || loanDetails?.userID,
          data: {
            mediaID: editModeMediaID,
            metadata: JSON.stringify(metaData)
          }
        },
        isLenderDashboard
      );

      if (response?.status) {
        notify({
          type: "success",
          title: "Success",
          text: "Document metadata updated successfully"
        });

        documentSliderProps?.close?.();
        documentSliderProps.reloadParent?.();
      } else {
        notify({
          type: "error",
          title: "Error",
          text: response.error || "Failed to update metadata"
        });
      }
      return;
    }

    // Original upload flow — iterate over all selected files
    const selectedFiles: File[] = Array.isArray(file)
      ? file
      : file
        ? [file]
        : [];

    if (selectedFiles.length === 0) return;

    const docNameValue =
      typeof documentName === "string" ? documentName : documentName?.value;

    const duplicateEntryAllowed = documentNames.find(
      (doc) => doc.value === docNameValue
    )?.duplicateEntryAllowed;

    const uploadedMediaIDs: string[] = [];
    const mediaIdsToDocumentIds: Record<string, string> = {};
    const docID = forCAMReportOnly
      ? CAM_REPORT_MEDIA_ID
      : (documentName as any)?.value || documentName;

    for (let i = 0; i < selectedFiles.length; i++) {
      const singleFile = selectedFiles[i];

      const mediaID = await uploadLenderMedia(singleFile);
      if (!mediaID) return; // stop if any upload fails

      uploadedMediaIDs.push(mediaID);
      mediaIdsToDocumentIds[mediaID] = docID;
    }

    const payload = {
      mediaIDs: uploadedMediaIDs,
      comment: data.comments,
      loanApplicationID: loanID,
      ...(isMFL_LAP && { duplicateEntryAllowed }),
      mediaIdsToDocumentIds,
      userID: isMFL_LAP ? selectedApplicant?.value : userID
    };

    const response = (await LoanApplicationService.uploadAllDocs({
      pageName: documentSliderProps.pageName,
      token: documentSliderProps.token,
      payload: payload,
      ...(isMFL_LAP && { userID: selectedApplicant?.value }),
      isPlatformDashboard: !documentSliderProps.isLenderDashboard,
      sourceEntityID
    })) as any;

    if (response?.status) {
      notify({
        type: "success",
        title: "Success",
        text: getNotificationText(uploadedMediaIDs)
      });

      documentSliderProps?.close?.();

      if (
        !forCAMReportOnly &&
        documentSliderProps?.location?.pathname?.includes("all-documents")
      ) {
        //In All Documents tab and using Add Document feature
        resetValues();
        documentSliderProps.reloadParent?.();
      }

      window.location.reload();
    } else {
      notify({
        type: "error",
        title: "Error",
        text: response.error
      });
    }
  };

  function resetValues() {
    setCategories([]);
    setDocumentNames([]);
    if (isMFL_LAP) {
      // isMFL_LAP && setValue("applicant", "");
      setSelectedApplicant(null);
    }

    setValue("category", "");
    setValue("documentName", "");
    setValue("file", null);
    setValue("comments", "");
    setSelectedDate(null);
    setDetails("");
    setOriginalXerox("");
  }

  const handleUploadClick = () => {
    const showCamera =
      permissions?.some((p) => p.toLowerCase() === "camera") ?? false;
    const showPhotoLibrary =
      !permissions ||
      permissions.some((p) => p.toLowerCase() === "choose_from_device");

    // If only one option is available, skip the modal
    if (showCamera && !showPhotoLibrary) {
      handleCameraCapture();
    } else if (!showCamera && showPhotoLibrary) {
      handlePhotoLibrary();
    } else {
      setShowUploadOptions(true);
    }
  };

  const handleCameraCapture = () => {
    setShowUploadOptions(false);
    setShowCamera(true);
  };

  const handlePhotoLibrary = () => {
    setShowUploadOptions(false);

    // Trigger the file input using the stored open function
    if (fileInputOpen) fileInputOpen();
  };

  const handleCameraCaptureComplete = (capturedFile: File) => {
    if (isUploadRestricted) {
      notify({
        type: "error",
        title: "Upload Limit Reached",
        text: "You have reached the maximum number of uploads allowed for this document type."
      });

      setShowCamera(false);
      return;
    }

    const existingFiles: File[] = Array.isArray(file)
      ? file
      : file instanceof File
        ? [file as File]
        : [];

    if (existingFiles.length >= maxFilesAllowed) {
      notify({
        type: "error",
        title: "Limit Reached",
        text: `You can attach a maximum of ${maxFilesAllowed} document${maxFilesAllowed > 1 ? "s" : ""} for this document type.`
      });

      setShowCamera(false);
      return;
    }

    const newFiles = [...existingFiles, capturedFile];
    setValue("file", newFiles as any, { shouldValidate: true });
    setShowCamera(false);
  };

  const handleCloseUploadOptions = () => setShowUploadOptions(false);

  const handleCloseCamera = () => setShowCamera(false);

  const handleDropRejected = (reason: any) => {
    //Do not clear existing files on rejection (duplicate or over-limit drop). The previously selected files should remain intact.
    // if (!editMode) setValue("file", null)

    if (reason === FileUploadRejectionReason.DUPLICATE) {
      notify({
        type: "error",
        title: "Duplicate File",
        text: "This file has already been selected. Please choose a different file."
      });
    } else if (reason === FileUploadRejectionReason.MAX_FILES) {
      notify({
        type: "error",
        title: "Limit Reached",
        text: `You can attach a maximum of ${maxFilesAllowed} document${maxFilesAllowed > 1 ? "s" : ""} for this document type.`
      });
    }
  };

  const handleFileInputClick = (openFn: () => void) => {
    if (!editMode) {
      if (isUploadRestricted) {
        notify({
          type: "error",
          title: "Upload Limit Reached",
          text: "You have reached the maximum number of uploads allowed for this document type."
        });

        return;
      }

      setFileInputOpen(() => openFn);
      handleUploadClick();
    }
  };

  const renderUploadCustomText = () => (
    <div id="upload-document-text" className="flex flex-col items-center">
      <div className="rounded h-7 w-7 border flex justify-center items-center">
        <CloudArrowUpIcon className="h-5 w-5 text-[#475467]" />
      </div>
      <div className="text-sm mt-2">
        Click to upload
        <span className="text-[#475467]"> or drag and drop</span>
      </div>
      <div className="text-xs text-[#475467] mt-0.5">
        Supported Formats: PDF, PNG, JPG, JPEG
      </div>
    </div>
  );

  const isFileUploadDisabled = () => {
    if (editMode || isUploadRestricted) return true;

    if (Array.isArray(uploadedFile)) {
      return uploadedFile.length >= maxFilesAllowed;
    }

    return !!uploadedFile && maxFilesAllowed === 1;
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={studioExperience ? "px-6 pb-6" : ""}
      >
        <div
          className={`sticky top-[-40px]  ${
            studioExperience ? "" : "bg-white p-4"
          } ${documentSliderProps.styles?.container || ""}`}
        >
          {!studioExperience && (
            <Title
              title={
                editMode
                  ? "Edit Document Metadata"
                  : `Add Document${forCAMReportOnly ? " (CAM Report Only)" : ""}`
              }
              close={() => {
                if (!forCAMReportOnly && !editMode) resetValues();

                documentSliderProps.close();
              }}
            />
          )}

          {!forCAMReportOnly && (
            <>
              <div className="mt-4">
                {isMFL_LAP && (
                  <>
                    <label className="block font-medium text-gray-700 text-xs mb-2">
                      Applicant Type{" "}
                      <span className="text-inherit text-red-700 ml-1">*</span>
                    </label>
                    <Controller
                      name="applicant"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (
                            !editMode &&
                            (!value ||
                              (typeof value === "object" && !value?.value))
                          ) {
                            return "Applicant Type is required";
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <ApplicantSelectionDropdown
                          loanDetails={loanDetails || {}}
                          styles={{
                            container: "!w-full"
                          }}
                          onChange={(applicant: any) => {
                            if (!editMode) {
                              setSelectedApplicant(applicant);
                              field.onChange(applicant?.value || applicant);

                              fetchUploadLimits(applicant?.value || applicant);
                            }
                          }}
                          selectedApplicant={selectedApplicant}
                          setSelectedApplicant={() => {}}
                          resetApplicantState={false}
                          placeholder={"Select Applicant Type"}
                          truncateName={false}
                          hasSearch={false}
                          studioExperience={studioExperience ?? false}
                          disabled={editMode}
                        />
                      )}
                    />
                    {errors.applicant?.message && (
                      <p className="mt-1 pl-2 text-xs text-red-700">
                        {errors.applicant?.message}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="mt-4">
                <Controller
                  name="category"
                  control={control}
                  rules={{
                    validate: (value) =>
                      !editMode &&
                      (!value || (typeof value === "object" && !value?.value))
                        ? "Category is required"
                        : true
                  }}
                  render={({ field }) => (
                    <DropdownWithSearch
                      options={categories.map((cat: any) => ({
                        name: cat.label,
                        value: cat.value
                      }))}
                      label="Category"
                      requiredIcon={!editMode}
                      selectedItems={
                        (selectedCategory as any) || { name: "", value: "" }
                      }
                      placeholder="Select Category"
                      onChange={(val: any) => {
                        if (!editMode) {
                          field.onChange(val);
                          setValue("documentName", ""); // Reset document name on category change
                          getDocumentsByCategory(val);
                        }
                      }}
                      errorText={errors.category?.message}
                      styles={{ container: "w-full mt-4" }}
                      disabled={editMode}
                    />
                  )}
                />
              </div>

              <div className="mt-4">
                <Controller
                  name="documentName"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (
                        !editMode &&
                        (!value || (typeof value === "object" && !value?.value))
                      ) {
                        return "Document Name is required";
                      }
                      return true;
                    }
                  }}
                  render={({ field }) => (
                    <DropdownWithSearch
                      options={documentNames.map((doc: any) => ({
                        name: doc.label,
                        value: doc.value,
                        isOverLayAddressRequired:
                          doc?.isOverLayAddressRequired || false,
                        permissions: doc?.uploadModeConfig
                      }))}
                      label="Document Name"
                      requiredIcon={!editMode}
                      hasSearch={false}
                      selectedItems={
                        (documentName as any) || { name: "", value: "" }
                      }
                      placeholder={
                        selectedCategory
                          ? "Select Document Name"
                          : "Select Category first"
                      }
                      onChange={(val: any) => {
                        if (!editMode) {
                          if (val.isOverLayAddressRequired)
                            setShowOverLayAddress(true);
                          else setShowOverLayAddress(false);

                          setPermissions(val?.permissions);
                          field.onChange(val);
                        }
                      }}
                      errorText={errors.documentName?.message}
                      styles={{ container: "w-full" }}
                      disabled={editMode || !selectedCategory}
                    />
                  )}
                />
              </div>
            </>
          )}

          <div className="mt-4">
            <FileUploadInput
              outsideView
              name="file"
              accept={{
                "image/*": [".jpeg", ".png", ".jpg"],
                "application/pdf": [".pdf"],
                "application/zip": [".zip"]
              }}
              styles={{
                inputContainer:
                  "bg-white !h-[130px] !min-h-[130px] !border-solid"
              }}
              maxFiles={maxFilesAllowed}
              onDropAccepted={() => {
                // Do nothing — FileUploadInput's onDrop already sets the full accumulated file array via setValue internally.
                // Overriding here with data[0] would break multi-file.
                // if (!editMode) {
                //   setValue("file", data[0] as any, { shouldValidate: true });
                // }
              }}
              onDropRejected={handleDropRejected}
              register={
                register("file", {
                  required: editMode ? false : "File is required"
                }) as any
              }
              viewDocument={(file: File) => {
                setObject(file);
                setObjectPath(null);
                setFileView(true);
              }}
              control={control}
              setValue={setValue}
              isRequired={!editMode}
              required={!editMode}
              showTakePhoto={showOverLayAddress}
              disabled={isFileUploadDisabled()}
              label={"Upload Document"}
              onOpenClick={handleFileInputClick}
              customText={renderUploadCustomText()}
            />
          </div>

          <div className="mt-4">
            <TextArea
              label="Comments"
              styles={{
                container: "rounded-md my-2",
                input: "resize-none text-md"
              }}
              placeholder="Add comments if any"
              rows={8}
              register={register("comments")}
              error={errors.comments}
              disabled={editMode}
            />
          </div>

          {isMFL_LAP &&
            scheduleDCategorySelected &&
            (showMetadataFields ||
              (!studioExperience && !forCAMReportOnly)) && (
              <div className="mt-4">
                <div className="mt-4 relative">
                  <Controller
                    name="scheduleDate"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (
                          scheduleDCategorySelected &&
                          (showMetadataFields ||
                            (!studioExperience && !forCAMReportOnly)) &&
                          !value
                        ) {
                          return "Date is required for Schedule D category";
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => (
                      <Picker
                        required
                        ariaRequired="true"
                        selected={field.value}
                        onChange={(date: Date | null) => {
                          if (date) {
                            const fixedDate = new Date(date);
                            fixedDate.setHours(12, 0, 0, 0);
                            setSelectedDate(fixedDate);
                            field.onChange(fixedDate);
                          } else {
                            setSelectedDate(null);
                            field.onChange(null);
                          }
                        }}
                        className={classNames(
                          "border-none text-xs outline-none py-0.5 text-center h-full w-full bg-gray-100"
                        )}
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        customInput={
                          <CustomDateInput
                            value={
                              field.value
                                ? (field.value as Date).toLocaleDateString(
                                    "en-GB",
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric"
                                    }
                                  )
                                : ""
                            }
                            onClick={() => {}}
                            disabled={false}
                            requiredIcon={true}
                          />
                        }
                        popperPlacement="bottom-start"
                        popperModifiers={[
                          {
                            name: "offset",
                            options: {
                              offset: [0, 6]
                            }
                          }
                        ]}
                      />
                    )}
                  />
                  {errors.scheduleDate?.message && (
                    <p className="mt-1 pl-2 text-xs text-red-700">
                      {errors.scheduleDate?.message}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <Controller
                    name="scheduleDetails"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (
                          scheduleDCategorySelected &&
                          (showMetadataFields ||
                            (!studioExperience && !forCAMReportOnly)) &&
                          (!value || value.trim() === "")
                        ) {
                          return "Details are required for Schedule D category";
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => (
                      <TextArea
                        label="Details"
                        styles={{
                          container: "rounded-md my-2",
                          input: "resize-none text-md"
                        }}
                        placeholder="Enter details"
                        rows={4}
                        value={field.value}
                        onChange={(
                          e: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setDetails(e.target.value);
                          field.onChange(e.target.value);
                        }}
                        requiredIcon
                      />
                    )}
                  />
                  {errors.scheduleDetails?.message && (
                    <p className="mt-1 pl-2 text-xs text-red-700">
                      {errors.scheduleDetails?.message}
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <Controller
                    name="scheduleOriginalXerox"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (
                          scheduleDCategorySelected &&
                          (showMetadataFields ||
                            (!studioExperience && !forCAMReportOnly))
                        ) {
                          const xeroxValue =
                            typeof value === "object" ? value?.value : value;
                          if (!xeroxValue) {
                            return "Original/Xerox selection is required for Schedule D category";
                          }
                        }
                        return true;
                      }
                    }}
                    render={({ field }) => (
                      <DropdownWithSearch
                        options={ORIGINAL_XEROX_OPTIONS || []}
                        hasSearch={false}
                        label="Original/Xerox"
                        requiredIcon
                        selectedItems={
                          (field.value as any) || { name: "", value: "" }
                        }
                        placeholder="Select Original/Xerox"
                        onChange={(val: any) => {
                          setOriginalXerox(val);
                          field.onChange(val);
                        }}
                        errorText={errors.scheduleOriginalXerox?.message}
                        styles={{ container: "w-full" }}
                      />
                    )}
                  />
                </div>
              </div>
            )}

          <div className="mt-4 flex justify-end">
            <Button
              type="submit"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              className="bg-indigo-600 text-white px-2 rounded hover:bg-indigo-700 transition"
            >
              {editMode ? "Update" : "Submit"}
            </Button>
          </div>
        </div>
      </form>

      <UploadedDocumentView
        objectFile={object as File}
        objectPath={objectPath}
        onClose={() => {
          setFileView(false);
          setObject(null);
          setObjectPath(null);
        }}
      />

      <UploadOptionsModal
        isOpen={showUploadOptions}
        onClose={handleCloseUploadOptions}
        onCamera={handleCameraCapture}
        onPhotoLibrary={handlePhotoLibrary}
        permissions={permissions}
      />

      <CameraCapture
        isOpen={showCamera}
        onClose={handleCloseCamera}
        onCapture={handleCameraCaptureComplete}
        onPhotoLibrary={handlePhotoLibrary}
        permissions={permissions}
      />
    </>
  );
};

export default AddNewDocumentSlider;
